import { makeField, NumInput, Panel } from "@/components/FieldInput";
import { AgeField } from "@/components/tabs/ClientFamilyTab";
import { CATEGORIES } from "@/lib/calc";
import { MARITAL } from "@/lib/schema";
import { money } from "@/lib/format";

const n = (v) => Number(v) || 0;
const QUICK_CATS = CATEGORIES.filter((c) => c !== "Taxes");

const QuickExpenses = ({ items, onChange, cur, total }) => {
  const rent = items.find((i) => i.id === "rent");
  const setRent = (v) => onChange(rent ? items.map((i) => (i.id === "rent" ? { ...i, current: v } : i))
    : [...items, { id: "rent", category: "Housing", item: "Mortgage / Rent", current: v, need: "N", retOverride: 0 }]);
  const setCat = (cat, v) => {
    const id = `qt-${cat}`;
    onChange(items.some((i) => i.id === id) ? items.map((i) => (i.id === id ? { ...i, current: v } : i))
      : [...items, { id, category: cat, item: `${cat} (total)`, current: v, need: "Y", retOverride: "" }]);
  };
  return (
    <div className="space-y-1.5">
      <div className="qexp-row"><span>Mortgage / Rent</span><NumInput id="qs-expense-rent" value={rent?.current} onChange={setRent} /></div>
      {QUICK_CATS.map((cat) => {
        const lines = items.filter((i) => i.category === cat && i.id !== "rent");
        const quick = lines.find((i) => i.id === `qt-${cat}`);
        const itemised = lines.some((i) => i !== quick && n(i.current) > 0);
        const sum = lines.reduce((a, i) => a + n(i.current), 0);
        const id = `qs-expense-${cat.toLowerCase()}`;
        return (
          <div key={cat} className="qexp-row">
            <span>{cat === "Housing" ? "Other housing" : cat}</span>
            {itemised
              ? <span className="read-value font-mono" data-testid={id} title="Itemised in Tab 2">{money(sum, cur)} <em className="field-hint">itemised in Tab 2</em></span>
              : <NumInput id={id} value={quick?.current} onChange={(v) => setCat(cat, v)} />}
          </div>
        );
      })}
      <div className="qexp-row is-total"><span>Total / month</span><span className="font-mono" data-testid="qs-expenses-total">{money(total, cur)}</span></div>
    </div>
  );
};

export const QuickStartTab = ({ d, s, set, cur, req }) => {
  const f = makeField(d, set, req, "qs-");
  const P = d.profile;
  const accts = d.investments.accounts;
  const updAcct = (i, patch) => set("investments.accounts", accts.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  return (
    <div className="space-y-2" data-testid="quick-start">
      <p className="text-xs text-slate-500">Enter the essentials for an instant result. Every field here is shared with Tabs 1–4, so use those tabs only for extra detail.</p>
      <div className="tab-grid">
        <Panel title="About the client" testId="qs-panel-client">
          <div className="form-grid form-grid-3">
            {f("profile.name", "Full name")}
            {f("profile.dob", "Date of birth", "date")}
            <AgeField id="qs-profile-age" reqId="profile-age" person={P} asOf={P.meetingDate} req={req} onChange={(v) => set("profile.age", v)} />
            {f("profile.maritalStatus", "Marital status", "text", { options: MARITAL })}
            {f("profile.spouse.name", "Spouse name")}
            {f("profile.retirementAge", "Retirement age", "number")}
          </div>
        </Panel>
        <Panel title="Income" testId="qs-panel-income">
          <div className="form-grid">
            {f("financial.income.clientAnnual", "Client gross income / year", "money")}
            {f("financial.income.clientTakeHome", "Client take-home / month", "money")}
            {f("financial.income.spouseAnnual", "Spouse gross income / year", "money")}
            {f("financial.income.spouseTakeHome", "Spouse take-home / month", "money")}
          </div>
        </Panel>
        <Panel title="Monthly expenses" testId="qs-panel-expenses" required missing={req.missing.has("panel-expenses")}>
          <QuickExpenses items={d.financial.expenses.items} onChange={(items) => set("financial.expenses.items", items)} cur={cur} total={s.exp.totalMonthly} />
        </Panel>
        <Panel title="Savings & government benefits" testId="qs-panel-savings">
          <div className="qacct-row exp-head"><span>Account</span><span>Current value</span><span>Monthly saving</span></div>
          {accts.map((a, i) => (
            <div key={a.id} className="qacct-row">
              <span className="text-sm text-slate-700">{a.type}{a.owner !== "Client" ? ` (${a.owner})` : ""}</span>
              <NumInput id={`qs-account-value-${i}`} value={a.value} onChange={(v) => updAcct(i, { value: v })} />
              <NumInput id={`qs-account-monthly-${i}`} value={a.monthly} onChange={(v) => updAcct(i, { monthly: v })} />
            </div>
          ))}
          <div className="form-grid mt-2">
            {f("retirement.cppMonthly", "CPP estimate / month", "money")}
            {f("retirement.yearsInCanada", "Years in Canada (OAS)", "number")}
          </div>
        </Panel>
        <Panel title="Existing coverage & mortgage (client)" className="col-span-full" testId="qs-panel-coverage">
          <div className="form-grid form-grid-4">
            {f("insurance.client.termLife", "Term life", "money")}
            {f("insurance.client.permanentLife", "Permanent life", "money")}
            {f("insurance.client.groupLife", "Group life", "money")}
            {f("insurance.client.criticalIllness", "Critical illness", "money")}
            {f("insurance.client.groupLtd", "Group LTD / month", "money")}
            {f("financial.liabilities.mortgage.amount", "Mortgage balance", "money")}
          </div>
        </Panel>
      </div>
    </div>
  );
};
